import React, { useContext, useEffect } from 'react';
import FacingTrouble from '../../components/FacingTrouble';
import AccountSetupForm from './AccountSetupForm';
import AddressInfoForm from './AddressInfoForm';
import PersonalInfoForm from './PersonalInfoForm';
import '../../../styles/irctcCreateAccountForms.css';
import {
  COUNTRIES_DETAILS,
  COUNTRIES_LIST,
  CREATE_ACCOUNT_FORM_ELEMENT_ID,
  FACING_TROUBLE_PAGE_TITLE,
  GENDER_RADIO,
  getFacingTroubleList,
  IRCTC_CLICK_EVENT,
  IRCTC_CREATE_ACCOUNT_COMPONENTS,
  IRCTC_PAGE_NAME,
  LANGUAGE_LIST,
  LANGUAGES_DETAILS,
  MARRIAGE_STATUS_RADIO,
  SERVER_URL
} from '../../../utils/RailsSEOConstants';
import { isEmpty } from '../../../utils/isEmpty';
import axios from 'axios';
import { checkValidation, validations } from '../../../utils/FormValidators';
import fecha from 'fecha';
import * as irctcCreateAccountActions from './../../../actions/irctcCreateAccountActions';
import { IrctcCreateAccountContext } from '../IrctcCreateAccountPage';
import { isMobile, scrollToElement } from '../../../utils/commonUtils';
import PropTypes from 'prop-types';
import { trackClickEventProp61, trackEvar22Event } from '../../../utils/omnitureUtils';

const IrctcCreateAccountForms = (props) => {
  const { customClass } = props;

  const context = useContext(IrctcCreateAccountContext);
  const {
    createAccountState: {
      userName,
      userEmail,
      mobileNumber,
      userNameErrorMessage,
      userEmailErrorMessage,
      mobileNumberErrorMessage,
      password,
      selectedSecurityQuestion,
      securityAnswer,
      firstName,
      lastName,
      selectedDate,
      selectedOccupation,
      selectedGender,
      selectedMaritalStatus,
      selectedPreferredLanguage,
      residentialAddress,
      alternateContact,
      selectedCountry,
      pinCode,
      pinCodeErrorMessage,
      cityList,
      stateList,
      postOfficeList,
      officePostOffice,
      officeCity,
      officeRegionalState,
      showAddressInfoOnMView,
      showPersonalInfoOnMView,
      showAccountInfoOnMView,
      scrollToErrorElement
    },
    createAccountDispatch
  } = context;

  const updateAccountSetupMView = (latestValue) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SET_SHOW_ACCOUNT_SETUP_MVIEW,
      data: latestValue
    });
  };
  const updatePersonalInfoMView = (latestValue) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SET_SHOW_PERSONAL_INFO_MVIEW,
      data: latestValue
    });
  };
  const updateAddressInfoMView = (latestValue) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SET_SHOW_ADDRESS_INFO_MVIEW,
      data: latestValue
    });
  };

  const expandAllOnMView = (invalidElement) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SCROLL_TO_ERROR_CREATE_ACCOUNT,
      data: invalidElement.elementId
    });
  };
  const getAdditionalScrollHeight = () => {
    const searchBarHeight = isMobile() ? -20 : -100; // negative to push content below search bar
    return searchBarHeight;
  };
  const performScroll = () => {
    // perform scroll to error element
    if (scrollToErrorElement) {
      scrollToElement(scrollToErrorElement, getAdditionalScrollHeight());
    }
    // reset scroll error element
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_RESET_SCROLL_ERROR_ELEMENT
    });
  };
  useEffect(() => {
    performScroll();
  }, [scrollToErrorElement]);

  const updateSecurityQuestionList = (latestSecurityQuestionList) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SET_SECURITY_QUESTION_LIST_CREATE_ACCOUNT,
      data: latestSecurityQuestionList
    });
  };
  const updateOccupationList = (latestOccupationList) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SET_OCCUPATION_LIST_CREATE_ACCOUNT,
      data: latestOccupationList
    });
  };
  const updateComponentToShow = (latestComponentToShow) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SET_COMPONENT_CREATE_ACCOUNT,
      data: latestComponentToShow
    });
  };
  const updateShowErrorCreateAccount = (latestShowErrorCreateAccount) => {
    createAccountDispatch({
      type: irctcCreateAccountActions.ACTION_SET_SHOW_ERROR_CREATE_ACCOUNT,
      data: latestShowErrorCreateAccount
    });
  };
  /***************** Fetching Data **************************/
  useEffect(() => {
    const fetchAccountSetupFormApiData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/getOptions`);
        if (!isEmpty(res) && !isEmpty(res.data)) {
          const { securityQuestionList, occupationList } = res.data;
          const latestSecurityQuestionList = securityQuestionList.map((item) => item.text);
          const latestOccupationList = occupationList.map((item) => item.text);
          updateSecurityQuestionList(latestSecurityQuestionList);
          updateOccupationList(latestOccupationList);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    };
    fetchAccountSetupFormApiData();
  }, []);

  const validateAddress = () => {
    return residentialAddress && residentialAddress.length >= 3 && residentialAddress.length <= 225;
  };

  const isInvalidInputValue = (inputFieldObject) => {
    const { fieldValue, fieldValidation, otherErrorMessage, otherChecksFailed } = inputFieldObject;
    if (otherErrorMessage || otherChecksFailed) {
      return true;
    } else if (fieldValidation) {
      return !checkValidation(fieldValue, fieldValidation);
    } else {
      return false;
    }
  };

  const getFieldValidationArray = () => {
    return [
      {
        fieldValue: userName,
        fieldValidation: validations.userName,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.userName,
        otherErrorMessage: userNameErrorMessage,
        otherChecksFailed: !userName
      },
      {
        fieldValue: userEmail,
        fieldValidation: validations.email,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.userEmail,
        otherErrorMessage: userEmailErrorMessage,
        otherChecksFailed: !userEmail
      },
      {
        fieldValue: mobileNumber,
        fieldValidation: validations.mobileNumber,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.mobileNumber,
        otherErrorMessage: mobileNumberErrorMessage,
        otherChecksFailed: !mobileNumber
      },
      {
        fieldValue: password,
        fieldValidation: validations.password,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.password,
        otherErrorMessage: undefined,
        otherChecksFailed: !password
      },
      {
        fieldValue: securityAnswer,
        fieldValidation: validations.securityAnswer,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.securityQuestion,
        otherErrorMessage: undefined,
        otherChecksFailed: !securityAnswer
      },
      {
        fieldValue: firstName,
        fieldValidation: validations.firstName,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.firstName,
        otherErrorMessage: undefined,
        otherChecksFailed: !firstName
      },
      {
        fieldValue: lastName,
        fieldValidation: validations.lastName,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.lastName,
        otherErrorMessage: undefined,
        otherChecksFailed: !lastName
      },
      {
        fieldValue: selectedDate,
        fieldValidation: undefined,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.selectedDate,
        otherErrorMessage: undefined,
        otherChecksFailed: !selectedDate
      },
      {
        fieldValue: residentialAddress,
        fieldValidation: undefined,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.residentialAddress,
        otherErrorMessage: undefined,
        otherChecksFailed: !validateAddress()
      },
      {
        fieldValue: alternateContact,
        fieldValidation: validations.mobileNumber,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.alternateContact,
        otherErrorMessage: undefined,
        otherChecksFailed: !alternateContact
      },
      {
        fieldValue: pinCode,
        fieldValidation: validations.pinCode,
        elementId: CREATE_ACCOUNT_FORM_ELEMENT_ID.pinCode,
        otherErrorMessage: pinCodeErrorMessage,
        otherChecksFailed: !pinCode
      }
    ];
  };
  const getInvalidElement = () => {
    const validateFields = getFieldValidationArray();
    const invalidElement = validateFields.find((element) => isInvalidInputValue(element));
    return invalidElement;
  };

  const createIRCTCAccountApi = async () => {
    const body = {
      userName,
      passWord: password,
      cnfPassWord: password,
      securityQuestion: selectedSecurityQuestion.toString(),
      securityAns: securityAnswer,
      prefLanguage: LANGUAGES_DETAILS[LANGUAGE_LIST[selectedPreferredLanguage]].key,
      firstName,
      lastName,
      email: userEmail,
      mobile: mobileNumber,
      dob: selectedDate && fecha.format(selectedDate, 'YYYYMMDD'),
      occupation: selectedOccupation.toString(),
      martialStatus: MARRIAGE_STATUS_RADIO[selectedMaritalStatus].value,
      nationalityId: COUNTRIES_DETAILS[COUNTRIES_LIST[selectedCountry]].key,
      gender: GENDER_RADIO[selectedGender].value,
      address: residentialAddress,
      countryId: COUNTRIES_DETAILS[COUNTRIES_LIST[selectedCountry]].key,
      otherCountry: COUNTRIES_LIST[selectedCountry],
      otherState: stateList[officeRegionalState],
      otherCity: cityList[officeCity],
      pinCode: pinCode,
      state: stateList[officeRegionalState],
      city: cityList[officeCity],
      postOffice: postOfficeList[officePostOffice],
      landlineNumber: alternateContact,
      officeCheckBoxEnabled: 'N',
      copyAddressResToOff: 'N',
      off_Address: residentialAddress,
      off_CountryId: COUNTRIES_DETAILS[COUNTRIES_LIST[selectedCountry]].key,
      off_otherCountry: COUNTRIES_DETAILS[COUNTRIES_LIST[selectedCountry]].key,
      off_otherState: stateList[officeRegionalState],
      off_otherCity: cityList[officeCity],
      off_PinCode: pinCode,
      off_State: stateList[officeRegionalState],
      off_City: cityList[officeCity],
      off_PostOffice: postOfficeList[officePostOffice],
      off_LandlineNumber: alternateContact
    };

    const res = await axios.post(`${SERVER_URL}/registerUser`, body);
    const { error } = res.data;

    if (!error) {
      updateComponentToShow(IRCTC_CREATE_ACCOUNT_COMPONENTS.otpVerificationForm);
      // Form Submitted Successfully
      trackClickEventProp61(IRCTC_PAGE_NAME, IRCTC_CLICK_EVENT.SEO_CREATE_ACCOUNT_SUCCESS);
    } else {
      trackEvar22Event(IRCTC_PAGE_NAME, `seo:CreateAccount_${error}`);
      trackClickEventProp61(IRCTC_PAGE_NAME, IRCTC_CLICK_EVENT.SEO_CREATE_ACCOUNT_FAILED);
    }
  };

  const handleIrctcCreateAccountSubmit = async () => {
    try {
      const invalidElement = getInvalidElement();
      if (invalidElement) {
        expandAllOnMView(invalidElement);
        updateShowErrorCreateAccount(true);
      } else {
        updateShowErrorCreateAccount(false);
        await createIRCTCAccountApi();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error ', err);
    }
    trackClickEventProp61(IRCTC_PAGE_NAME, IRCTC_CLICK_EVENT.CREATE_ACCOUNT);
  };
  const isMobileDevice = isMobile();
  return (
    <div className={`${customClass} irctcDetailsComp`}>
      <div className={`irctcDetailsComp__cntnt`}>
        <h2
          className={showAccountInfoOnMView ? 'noBdr closeInfo' : 'openInfo'}
          onClick={() => updateAccountSetupMView(!showAccountInfoOnMView)}
        >
          Account Setup
        </h2>
        {showAccountInfoOnMView && isMobileDevice && <AccountSetupForm />}
        {!isMobileDevice && <AccountSetupForm />}
        <h2
          className={showPersonalInfoOnMView ? 'noBdr closeInfo' : 'openInfo'}
          onClick={() => updatePersonalInfoMView(!showPersonalInfoOnMView)}
        >
          Personal Info
        </h2>
        {showPersonalInfoOnMView && isMobileDevice && <PersonalInfoForm />}
        {!isMobileDevice && <PersonalInfoForm />}
        <h2
          className={showAddressInfoOnMView ? 'noBdr closeInfo' : 'openInfo'}
          onClick={() => updateAddressInfoMView(!showAddressInfoOnMView)}
        >
          Address Info
        </h2>
        {showAddressInfoOnMView && isMobileDevice && <AddressInfoForm />}
        {!isMobileDevice && <AddressInfoForm />}

        <button className="blueButtonRails fullWidth append_bottom20" onClick={handleIrctcCreateAccountSubmit}>
          Create Account
        </button>
      </div>
      <FacingTrouble facingTroubleList={getFacingTroubleList(FACING_TROUBLE_PAGE_TITLE.CREATE_ACCOUNT_TITLE)} />
    </div>
  );
};

IrctcCreateAccountForms.propTypes = {
  customClass: PropTypes.string
};

export default IrctcCreateAccountForms;
